import { useLayoutEffect, useRef } from 'react'
import { useFeatures } from '@/contexts/root-store-context'
import { type Registration } from '@/features/shell/theming/ChromeThemeManager'
import { type ChromeThemeLayer } from '@/features/shell/theming'
import type { MotionValue } from 'motion/react'

/**
 * Manages a `<meta name="theme-color">` element to customize the theme color
 * of the browser chrome — as in, the general term for the browser UI
 * surrounding the web content, not just the Chrome browser — on supported platforms.
 *
 * @param color The color to use. If `null`, it will not set a color.
 * @param layer The layer. There should only be as many uses of this hook
 * as there are layers.
 */
export function useChromeThemeColor(
  color: string | MotionValue<string> | null,
  layer: ChromeThemeLayer,
) {
  const registrationRef = useRef<Registration>(null)
  const initialColorRef = useRef(color)
  const {
    internal: { chromeThemeManager },
  } = useFeatures(({ shell }) => ({ internal: shell.internal }))

  useLayoutEffect(() => {
    if (!isThemeColorSupported) {
      return
    }

    registrationRef.current ??= chromeThemeManager.register(
      initialColorRef.current,
      layer,
    )

    return () => {
      if (registrationRef.current) {
        chromeThemeManager.unregister(registrationRef.current)
        registrationRef.current = null
      }
    }
  }, [chromeThemeManager, layer])

  useLayoutEffect(() => {
    if (!isThemeColorSupported) {
      return
    }

    registrationRef.current?.update(color)
  }, [color])
}

/**
 * Whether the `theme-color` feature is supported by the current user agent.
 */
export const isThemeColorSupported = checkIsThemeColorSupported()

/**
 * Checks whether the `theme-color` variable is supported.
 * There's no way to feature-detect this, so we'll have to do it
 * based on known browser support.
 */
function checkIsThemeColorSupported() {
  const ua = navigator.userAgent

  // Safari 15+ on iOS and macOS
  if (ua.includes('Safari') && !ua.includes('Chrome')) {
    const versionMatch = /Version\/(\d+)/.exec(ua)
    if (versionMatch && parseInt(versionMatch[1], 10) >= 15) {
      return true
    }
  }

  // Chrome on iOS and macOS
  if (ua.includes('Chrome') && ua.includes('Safari')) {
    const versionMatch = /Chrome\/(\d+)/.exec(ua)
    if (versionMatch && parseInt(versionMatch[1], 10) >= 90) {
      // Must be in stand-alone mode.
      if (window.matchMedia('(display-mode: standalone)').matches) {
        return true
      }
    }
  }

  // Chrome for Android (all versions)
  if (ua.includes('Android') && ua.includes('Chrome/')) {
    return true
  }

  // Samsung Internet 6.2+
  if (ua.includes('SamsungBrowser/')) {
    const versionMatch = /SamsungBrowser\/(\d+)/.exec(ua)
    if (versionMatch && parseInt(versionMatch[1], 10) >= 6) {
      return true
    }
  }

  // Vivaldi browser (both mobile and desktop)
  return ua.includes('Vivaldi')
}
